import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getComics } from "../../../services/readingOrdersApi";
import { uniqByStory } from "../../../core/utils";

/**
 * Get the logic to make a list of comics
 */
export default function useComicsPaginatedList(readingOrderId) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [comics, setComics] = useState([]);
  const queryClient = useQueryClient();

  const orderQuery = useQuery(
    ["readingOrderComics", readingOrderId, currentPage],
    () => getComics(readingOrderId, currentPage),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        // Flatten the newly fetched data with the existing data
        const newComics = data.data || [];
        setComics(uniqByStory(newComics.sort((a, b) => a.order - b.order)));
        setCurrentPage(data.meta.currentPage);
        setTotalPages(data.meta.lastPage);
        setTotalItems(data.meta.total);
      },
      enabled: !!readingOrderId,
    }
  );

  const onClickFirst = () => {
    setCurrentPage(1);
    setComics([]);
    queryClient.invalidateQueries(["readingOrderComics", readingOrderId]);
  };

  const onClickLast = () => {
    setCurrentPage(totalPages);
    setComics([]);
    queryClient.invalidateQueries(["readingOrderComics", readingOrderId]);
  };

  const loadMoreItems = orderQuery.isFetching
    ? () => {}
    : () => orderQuery.fetchNextPage();

  const itemData = useMemo(() => comics, [comics]);

  const handleJumpTo = (e) => {
    setCurrentPage(parseInt(e.target.value, 10));
    setComics([]);
    queryClient.invalidateQueries(["readingOrderComics", readingOrderId]);
  };

  return {
    comics,
    loadMoreItems,
    itemData,
    currentPage,
    totalPages,
    totalItems,
    handleJumpTo,
    onClickLast,
    onClickFirst,
    isLoadingComics: orderQuery.isLoading,
  };
}
